import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { COLORS } from '../data/mockData';
import { CONTACT_EMAIL } from '../data/contact';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import VideoWithSoundToggle from '../components/VideoWithSoundToggle';
import { HIPAA_DISCLAIMER, HIPAA_POLICY_SECTIONS, HIPAA_POLICY_VERSION } from '../data/hipaaPolicy';
import { Heart, Shield, Users, Microscope, Award, ArrowRight, Leaf, Target, Globe, ShieldCheck, Lock, EyeOff, ScrollText, UserCheck, Bell } from 'lucide-react';

const HIPAA_SECTION_ICONS = {
  commitment: ShieldCheck,
  encryption: Lock,
  anonymization: EyeOff,
  access: ScrollText,
  rights: UserCheck,
  breach: Bell,
};

const TEAM = [
  { name: 'Dr. Robert H. Cohen', role: 'Chief Medical Officer & CEO, Co-Founder', bio: 'Gerontologist with 15+ years in longevity research at Stanford.', color: '#6B8E23' },
  { name: 'Dr. Robert H. Cohen', role: 'CEO & Co-Founder', bio: 'Former health-tech VP at Google, MIT Computer Science.', color: '#4682B4' },
  { name: 'Dr. Robert H. Cohen', role: 'Head of AI & Data', bio: 'PhD in Biomedical AI, published 40+ peer-reviewed papers.', color: '#8B008B' },
  { name: 'Dr. Robert H. Cohen', role: 'VP of Engineering', bio: '12 years building HIPAA-compliant health platforms.', color: '#DAA520' },
  { name: 'Dr. Robert H. Cohen', role: 'Head of Research', bio: 'NIA research fellow specializing in epigenetic aging markers.', color: '#3CB371' },
  { name: 'Dr. Robert H. Cohen', role: 'CTO, Secretary, CFO & Co-Founder', bio: 'UX leader who built health apps used by 5M+ people.', color: '#B22222' },
];

const VALUES = [
  { icon: Shield, title: 'HIPAA First', desc: 'Your health data is sacred. We exceed regulatory requirements for privacy and security.' },
  { icon: Microscope, title: 'Evidence-Based', desc: 'Every recommendation backed by peer-reviewed research from NIA, Stanford, and Lifespan.io.' },
  { icon: Users, title: 'Accessible to All', desc: 'From our free Explorer tier to Elite coaching, longevity science should be for everyone.' },
  { icon: Heart, title: 'AI-Powered', desc: 'GPT-4o analyzes your unique profile to deliver truly personalized health insights.' },
];

export default function AboutPage() {
  const location = useLocation();

  // Support deep links like /about#hipaa (e.g. from the consent dialog).
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: '100vh' }}>
      <SiteHeader />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(180deg, #F9F6F0 0%, #E8ECD7 50%, #F9F6F0 100%)', padding: '64px 20px 56px', textAlign: 'center' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2, textTransform: 'uppercase' }}>ABOUT US</span>
          <h1 data-testid="about-title" style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 900, color: COLORS.textPrimary, lineHeight: 1.15, letterSpacing: -1, marginTop: 12, fontFamily: 'Outfit, sans-serif' }}>
            Extending Human Healthspan<br />Through Science & AI
          </h1>
          <p style={{ fontSize: 'clamp(16px, 2vw, 18px)', color: COLORS.textSecondary, lineHeight: 1.6, marginTop: 16, maxWidth: 640, margin: '16px auto 0' }}>
            Founded in 2024, ThriveLonger.ai combines cutting-edge longevity research with personalized AI coaching to help people live longer, healthier, and happier lives.
          </p>
        </div>
      </section>

      {/* INTRO VIDEO (under banner, top-middle, 65% size) */}
      <section data-testid="about-intro-video-section" style={{ padding: '20px 20px 0' }}>
        <VideoWithSoundToggle
          src="/media/about-intro.mp4"
          testId="about-intro-video"
          aspectRatio="9 / 16"
          maxWidth={252}
          objectFit="contain"
        />
      </section>

      {/* Mission */}
      <section style={{ padding: '56px 20px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', gap: 40, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1 1 400px' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2 }}>OUR MISSION</span>
            <h2 style={{ fontSize: 30, fontWeight: 800, color: COLORS.textPrimary, marginTop: 12, fontFamily: 'Outfit, sans-serif', lineHeight: 1.2 }}>
              Extend healthspan through personalized, AI-driven health optimization
            </h2>
            <p style={{ fontSize: 16, color: COLORS.textSecondary, lineHeight: 1.7, marginTop: 16 }}>
              We combine cutting-edge longevity research with advanced AI to give every person a clear, actionable path to a longer, healthier life. Our 9-category health scoring engine analyzes over 100 factors to predict and improve your healthspan.
            </p>
            <div style={{ display: 'flex', gap: 24, marginTop: 24 }}>
              {[{ num: '100+', label: 'Health Factors' }, { num: '50K+', label: 'Users' }, { num: '9', label: 'Score Categories' }].map((s, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: COLORS.primary, fontFamily: 'Outfit, sans-serif' }}>{s.num}</div>
                  <div style={{ fontSize: 12, color: COLORS.textSecondary }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ flex: '0 1 360px', backgroundColor: COLORS.surface, borderRadius: 24, padding: 32, boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <Leaf size={32} color={COLORS.primary} />
              <span style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>ThriveLonger.ai</span>
              <img src="/thrivelonger-logo.png" alt="ThriveLonger logo" data-testid="brand-logo-about" style={{ height: 36, width: 'auto', display: 'block' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { icon: Target, text: 'Personalized health scoring' },
                { icon: Award, text: 'Research-backed recommendations' },
                { icon: Users, text: 'Community-driven challenges' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: `${COLORS.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={18} color={COLORS.primary} />
                    </div>
                    <span style={{ fontSize: 15, color: COLORS.textPrimary, fontWeight: 500 }}>{item.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section style={{ padding: '56px 20px', backgroundColor: COLORS.surfaceSecondary }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2 }}>OUR VALUES</span>
          <h2 style={{ fontSize: 30, fontWeight: 800, color: COLORS.textPrimary, marginTop: 12, fontFamily: 'Outfit, sans-serif' }}>What We Stand For</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 20, marginTop: 32 }}>
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} style={{ backgroundColor: COLORS.surface, borderRadius: 20, padding: 24, textAlign: 'left' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, backgroundColor: `${COLORS.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                    <Icon size={24} color={COLORS.primary} />
                  </div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginBottom: 6 }}>{v.title}</h3>
                  <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.5 }}>{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HIPAA Compliance & Data Protection */}
      <section id="hipaa" data-testid="about-hipaa-section" style={{ padding: '56px 20px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2 }}>PRIVACY &amp; SECURITY</span>
            <h2 data-testid="about-hipaa-title" style={{ fontSize: 30, fontWeight: 800, color: COLORS.textPrimary, marginTop: 12, fontFamily: 'Outfit, sans-serif' }}>
              HIPAA Compliance &amp; Data Protection
            </h2>
            <p style={{ fontSize: 15, color: COLORS.textSecondary, lineHeight: 1.6, maxWidth: 720, margin: '12px auto 0' }}>
              How ThriveLonger.ai encrypts, anonymizes, and audits your medical records to
              safeguard them and comply with HIPAA laws. Policy version {HIPAA_POLICY_VERSION}.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20, marginTop: 32 }}>
            {HIPAA_POLICY_SECTIONS.map(section => {
              const Icon = HIPAA_SECTION_ICONS[section.id] || ShieldCheck;
              return (
                <div key={section.id} data-testid={`hipaa-policy-${section.id}`}
                  style={{ backgroundColor: COLORS.surface, borderRadius: 20, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', textAlign: 'left' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: `${COLORS.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                    <Icon size={22} color={COLORS.primary} />
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginBottom: 6 }}>{section.title}</h3>
                  <p style={{ fontSize: 13.5, color: COLORS.textSecondary, lineHeight: 1.6, margin: 0 }}>{section.body}</p>
                </div>
              );
            })}
          </div>

          <div style={{ backgroundColor: '#FFF8E1', border: '1px solid #F0E0A0', borderRadius: 16, padding: 20, marginTop: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#A65500', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8, fontFamily: 'Outfit, sans-serif' }}>Disclaimer</div>
            <p style={{ fontSize: 13.5, color: COLORS.textPrimary, lineHeight: 1.65, margin: 0 }}>{HIPAA_DISCLAIMER}</p>
          </div>

          <p style={{ fontSize: 13, color: COLORS.textSecondary, textAlign: 'center', marginTop: 20 }}>
            Questions about your data? Contact <strong>{CONTACT_EMAIL}</strong>.
          </p>
        </div>
      </section>

      {/* Team */}
      <section style={{ padding: '56px 20px', backgroundColor: COLORS.surfaceSecondary }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2 }}>OUR TEAM</span>
          <h2 style={{ fontSize: 30, fontWeight: 800, color: COLORS.textPrimary, marginTop: 12, fontFamily: 'Outfit, sans-serif' }}>Led by Experts in Longevity Science</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20, marginTop: 32 }}>
            {TEAM.map((t, i) => (
              <div key={i} style={{ backgroundColor: COLORS.surface, borderRadius: 20, padding: 24, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ width: 64, height: 64, borderRadius: 32, backgroundColor: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: '#FFF' }}>{t.name.split(' ').map(w => w[0]).join('')}</span>
                </div>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>{t.name}</h3>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.primary, marginTop: 2 }}>{t.role}</div>
                <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.5, marginTop: 10 }}>{t.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section style={{ padding: '56px 20px', backgroundColor: COLORS.textPrimary }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2 }}>RESEARCH PARTNERS</span>
          <h2 style={{ fontSize: 30, fontWeight: 800, color: '#FFF', marginTop: 12, fontFamily: 'Outfit, sans-serif' }}>Backed by World-Class Institutions</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16, marginTop: 32 }}>
            {[
              { name: 'National Institute on Aging', abbr: 'NIA' },
              { name: 'Stanford Center on Longevity', abbr: 'SCL' },
              { name: 'Lifespan.io Research', abbr: 'LSR' },
              { name: 'Harvard T.H. Chan School', abbr: 'HTC' },
              { name: 'Mayo Clinic Research', abbr: 'MCR' },
              { name: 'Blue Zones Project', abbr: 'BZP' },
            ].map((p, i) => (
              <div key={i} style={{ backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 16, padding: 24, textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: COLORS.primary }}>{p.abbr}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 6 }}>{p.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg, #6B8E23, #4a6e18)', padding: '56px 20px', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: '#FFF', fontFamily: 'Outfit, sans-serif' }}>Ready to Start Your Longevity Journey?</h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.8)', marginTop: 10, marginBottom: 24 }}>Join thousands optimizing their healthspan with ThriveLonger.ai</p>
          <Link to="/register" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, backgroundColor: '#FFF', borderRadius: 9999, padding: '16px 32px', textDecoration: 'none' }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.primary }}>Get Started Free</span>
            <ArrowRight size={20} color={COLORS.primary} />
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
